import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import SnippetFolderIcon from "@mui/icons-material/Folder";
import { keyframes } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import {
  ConfirmResponse,
  useConfirmDialog,
} from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import CreateFeeScheduleCategory from "_shared/fee-schedules/categories/_views/create-fee-schedule-category";
import FeeScheduleCategoryList from "_shared/fee-schedules/categories/_views/fee-schedule-category-list";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import { CanceledError } from "axios";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton
} from "material-react-table";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency } from "work/_shared/masked-fields";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import {
  FeeScheduleTemplateOrderField,
  FeeScheduleTemplateOrderParameters,
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import FeeScheduleTemplateQueryAPIService from "work/fee-schedule-templates/_api/queries/fee-schedule-template-query-api-service";
import FeeScheduleExistingTemplate from "work/fee-schedule-templates/_views/lists/fee-schedule-template-list";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import FeeScheduleCategory from "../../../../_shared/fee-schedules/categories/fee-schedule-category";
import { ProposalIssue } from "work/proposals/issues/proposal-issues";
import { MRTNoMemo } from "_shared/_views/tables/table";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
  marginBottom: theme.spacing(2),
}));
const RowTitle = styled(Typography)(() => ({
  fontSize: "1em",
  fontWeight: "500",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const RowActionsContainer = styled("div")(() => ({
  whiteSpace: "nowrap",
}));

const columnHelper = createMRTColumnHelper<
  FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
>();

type FeeScheduleTemplatesTableProps = {
  className?: string;
  categories:
  | Array<FeeScheduleCategory>
  | Array<FeeScheduleCategoryMaskedCurrency>;
  disableEditing?: boolean;
  disableCategorySelection?: boolean;
  hideFromTemplateButton?: boolean;
  hideAddButton?: boolean;
  hideExistingButton?: boolean;
  hideCommentButton?: boolean;
  hideActionsColumn?: boolean;
  issues?: ProposalIssue[];
  commentThreads?: CommentThread[];
  disableComments?: boolean;
  commentsToolTip: string;
  activeReviewField?: ProposalField;
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
  onCategoryAdded?: (
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ) => void;
  onCategoryUpdated?: (
    originalName: FeeScheduleCategoryName | null,
    updatedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ) => void;
  onCategoryRemoved?: (name: FeeScheduleCategoryName) => void;
  onExistingTemplateSelected?: (
    template: FeeScheduleTemplate,
    replace: boolean,
  ) => void;
};

export default function FeeScheduleTemplatesTable(
  props: Readonly<FeeScheduleTemplatesTableProps>,
) {
  const {
    className,
    categories,
    disableEditing,
    disableCategorySelection,
    hideFromTemplateButton,
    hideAddButton,
    hideExistingButton,
    hideCommentButton,
    hideActionsColumn,
    issues,
    commentThreads,
    disableComments,
    commentsToolTip,
    activeReviewField,
    onCommentsClicked,
    onCategoryAdded,
    onCategoryUpdated,
    onCategoryRemoved,
    onExistingTemplateSelected,
  } = props;

  const [existingTemplates, setExistingTemplates] = useState<
    FeeScheduleTemplate[]
  >([]);
  const [existingCategories, setExistingCategories] = useState<
    FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[]
  >([]);
  const [query, setQuery] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isLoading, setIsLoading] = useState(false);

  const session = useSession();
  const confirm = useConfirmDialog();
  const { openDialog, popDialog } = useDialog();

  const paginationParameters = useMemo(() => PaginationParameters.create({
    pageIndex: 0,
    pageSize: 10,
  }), []);

  const getExistingTemplatesAndCategories = useCallback(async () => {
    if (!session.user?.isCompanyManager) return;

    setIsLoading(true);
    try {
      const entityId = session.user.legalEntityId;

      const filterParameters = new FeeScheduleTemplateFilterParameters({
        entityId,
      });
      const orderParameters = new FeeScheduleTemplateOrderParameters({
        orderBy: FeeScheduleTemplateOrderField.Name,
        orderDir: OrderDirection.Descending,
      });

      const templateQuery: FeeScheduleTemplateQuery =
        new FeeScheduleTemplateQuery({
          filterParameters,
          orderParameters,
          paginationParameters,
        });
      const feeScheduleService = new FeeScheduleTemplateQueryAPIService(
        session,
      );

      const templatesJSON =
        await feeScheduleService.getFeeScheduleTemplatesByQuery(templateQuery); // TODO: use store
      const templates = templatesJSON.data.map((templateData) =>
        FeeScheduleTemplate.fromJSON(templateData),
      );
      setExistingTemplates(templates);

      const existingCategories: FeeScheduleCategory[] = [];
      const existingCategoriesMasked: FeeScheduleCategoryMaskedCurrency[] = [];
      templates.forEach((template) => {
        template.categories.forEach((category) => {
          if (category instanceof FeeScheduleCategoryMaskedCurrency)
            existingCategoriesMasked.push(category);
          else existingCategories.push(category);
        });
      });

      setExistingCategories(
        existingCategories.length > 0
          ? existingCategories
          : existingCategoriesMasked,
      );
      setRowCount(existingCategories.length);
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [paginationParameters, session]);

  useEffect(() => {
    void getExistingTemplatesAndCategories();
  }, [getExistingTemplatesAndCategories]);

  const handleRemoveButtonClicked = useCallback(
    async (
      event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
      category:
        | FeeScheduleCategory
        | FeeScheduleCategoryMaskedCurrency
        | undefined
        | null,
    ) => {
      event.stopPropagation();

      if (!category) return;

      const response = await confirm({
        title: "Delete Fee Schedule Category?",
        message: `Delete the ${category.name} fee schedule category?`,
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      onCategoryRemoved?.(category.name);
    },
    [confirm, onCategoryRemoved],
  );

  const handleExistingTemplateSelected = useCallback(
    async (template: FeeScheduleTemplate) => {
      let replace: boolean = false;

      if (categories.length > 0) {
        const response = await confirm({
          title: "Replace Or Add Categories?",
          message: `Do you want to replace the current categories with the selected template's categories or add the selected template's categories to the current categories?`,
          okButtonText: "Replace",
          alternativeOkText: "Add",
          cancelButtonText: "Cancel",
        });

        if (response === ConfirmResponse.Cancel) return;

        replace = response === ConfirmResponse.Ok;
      }

      popDialog();
      onExistingTemplateSelected?.(template, replace);
    },
    [categories.length, confirm, onExistingTemplateSelected, popDialog],
  );

  const beginAddCategory = useCallback(() => {
    openDialog({
      title: "Create Fee Schedule Category",
      component: (
        <CreateFeeScheduleCategory
          onCategoryAdded={onCategoryAdded}
          popDialog={popDialog}
        />
      ),
    });
  }, [onCategoryAdded, openDialog, popDialog]);

  const beginEditCategory = useCallback(
    (
      category:
        | FeeScheduleCategory
        | FeeScheduleCategoryMaskedCurrency
        | undefined
        | null,
    ) => {
      if (!category || disableEditing) return;

      openDialog({
        title: "Edit Fee Schedule Category",
        component: (
          <CreateFeeScheduleCategory
            category={category}
            onCategoryUpdated={onCategoryUpdated}
            popDialog={popDialog}
          />
        ),
      });
    },
    [disableEditing, onCategoryUpdated, openDialog, popDialog],
  );

  const handleExistingTemplateClicked = useCallback(() => {
    openDialog({
      title: "Select Existing Fee Schedule Template",
      component: (
        <FeeScheduleExistingTemplate
          onTemplateSelected={handleExistingTemplateSelected}
        />
      ),
    });
  }, [handleExistingTemplateSelected, openDialog]);

  const handleExistingCategoriesSelected = useCallback(
    (categories: FeeScheduleCategory[]) => {
      popDialog();
      categories.forEach((category) => {
        onCategoryAdded?.(category);
      });
    },
    [onCategoryAdded, popDialog],
  );

  const handleExistingCategoryClicked = useCallback(() => {
    openDialog({
      title: "Add Existing Fee Schedule Category",
      component: (
        <FeeScheduleCategoryList
          categories={existingCategories}
          disabledCategories={categories}
          onCategoriesSelected={handleExistingCategoriesSelected}
        />
      ),
    });
  }, [
    existingCategories,
    categories,
    openDialog,
    handleExistingCategoriesSelected,
  ]);

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (category) => <RowTitle>{category.name.value}</RowTitle>,
        {
          header: "Name",
          id: "name",
        },
      ),
      columnHelper.accessor((category) => category.description?.value, {
        header: "Description",
        id: "description",
        enableSorting: false,
      }),
      columnHelper.accessor(
        (category) => {
          let fixedValue;
          if (category instanceof FeeScheduleCategoryMaskedCurrency) {
            fixedValue = "Masked";
          } else if (category?.fee?.unit) {
            fixedValue = "No";
          } else {
            fixedValue = "Yes";
          }
          return fixedValue;
        },
        {
          header: "Fixed?",
          id: "fixedFee",
          size: 50,
          enableSorting: false,
        },
      ),
      columnHelper.accessor(
        (category) => {
          let feeValue;
          if (category instanceof FeeScheduleCategoryMaskedCurrency) {
            feeValue = "Masked";
          } else if (category.fee == null) {
            feeValue = (
              <Typography color="warning">{`Deferred to ${session.context?.viewingAsVendor ? "client" : "vendor"
                }`}</Typography>
            );
          } else {
            feeValue = new Intl.NumberFormat(navigator.language, {
              style: "currency",
              currency: category.fee?.rate?.currency.toString() ?? "USD",
            }).format(category.fee?.rate?.amount ?? 0);
          }
          return feeValue;
        },
        {
          header: "Fee",
          id: "fee",
          size: 50,
        },
      ),
      columnHelper.accessor(
        (category) =>
          category instanceof FeeScheduleCategoryMaskedCurrency
            ? "Masked"
            : (category.fee?.unit ?? <Icon>remove</Icon>),
        {
          header: "Per",
          id: "unit",
          size: 50,
          enableSorting: false,
        },
      ),
      columnHelper.accessor(
        (category) =>
          category.billingCode?.value &&
            category.billingCode.value.length > 0 ? (
            category.billingCode.value
          ) : (
            <Icon>remove</Icon>
          ),
        {
          header: "Code",
          id: "billingCode",
          size: 50,
          enableSorting: false,
        },
      ),
    ],
    [session.context?.viewingAsVendor],
  );

  const tableOptions = useMemo<
    MRT_TableOptions<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  >(
    () => ({
      columns,
      data: categories,
      muiPaginationProps: {
        color: "primary",
        shape: "rounded",
        showRowsPerPage: false,
        variant: "outlined",
      },
      paginationDisplayMode: "pages",
      enableRowSelection: false,
      enableTableHead: true,
      getRowId: (row) => row?.id?.value,
      initialState: {
        showColumnFilters: false,
        columnPinning: { right: ["mrt-row-actions"] },
        density: "compact",
      },
      enableColumnPinning: true,
      manualFiltering: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      enableRowActions: !hideActionsColumn,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setQuery,
      onSortingChange: setSorting,
      rowCount,
      state: {
        columnFilters,
        globalFilter: query,
        isLoading,
        sorting,
      },
      muiTableProps: ({ table }) => ({
        sx: {
          borderSpacing: table
            .getRowModel()
            .rows.some((row) => row.id === activeReviewField?.id?.value)
            ? "3px"
            : 0,
        },
      }),
      muiTableBodyRowProps: ({ row }) => {
        return disableCategorySelection
          ? {
            sx: {
              "& tr.MuiTableRow-root": {
                cursor: "default !important",
              },
              "& > td": {
                hover: !disableCategorySelection,
              }
            }
          }
          : {
            onClick: () => beginEditCategory(row.original),
            sx: {
              animation: row?.original?.id?.isEqualTo(activeReviewField?.id)
                ? `${pulseBorder} 1.5s infinite`
                : "none",
              cursor: "pointer",
            },
          };
      },
      renderEmptyRowsFallback: () => {
        return (
          <Box>
            <Typography
              sx={{ p: 4, display: "flex", justifyContent: "center" }}
              variant="body1"
              color="textSecondary"
            >
              No fee schedule categories
            </Typography>
          </Box>
        );
      },
      renderTopToolbarCustomActions: () => {
        return (
          <ActionsContainer>
            {!disableEditing && !hideAddButton && (
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                size="small"
                onClick={beginAddCategory}
              >
                Add New Category
              </Button>
            )}
            {!disableEditing && !hideExistingButton && (
              <Button
                startIcon={<SnippetFolderIcon />}
                variant="outlined"
                disabled={existingCategories.length === 0}
                size="small"
                onClick={handleExistingCategoryClicked}
              >
                Add Existing Category
              </Button>
            )}
            {!disableEditing && !hideFromTemplateButton && (
              <Button
                startIcon={<FileCopyIcon />}
                size="small"
                disabled={existingTemplates.length === 0}
                onClick={handleExistingTemplateClicked}
                variant="outlined"
              >
                From Existing Template
              </Button>
            )}
          </ActionsContainer>
        );
      },
      renderToolbarInternalActions: ({ table }) => (
        <Box>
          <ToggleGlobalFilterButton table={table} />
          <ToggleFiltersButton table={table} />
          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
          {!hideCommentButton && (
            <CommentsButton
              commentThreads={commentThreads}
              disabled={disableComments}
              field={ProposalField.FeeSchedule}
              isActive={activeReviewField?.isEqualTo(ProposalField.FeeSchedule)}
              toolTip={commentsToolTip}
              onCommentsClicked={
                onCommentsClicked
                  ? () => {
                    onCommentsClicked?.(ProposalField.FeeSchedule);
                  }
                  : undefined
              }
            />
          )}
        </Box>
      ),
      renderRowActions: ({ row }) => {
        return (
          <RowActionsContainer>
            {!disableEditing && (
              <Tooltip title="Remove Category">
                <span>
                  <IconButton
                    onClick={(event) =>
                      handleRemoveButtonClicked(event, row.original)
                    }
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!hideCommentButton && (
              <CommentsButton
                field={ProposalField.FeeScheduleCategory(row.original.id)}
                isActive={activeReviewField?.isEqualTo(
                  ProposalField.FeeScheduleCategory(row.original.id),
                )}
                commentThreads={commentThreads}
                disabled={disableComments}
                toolTip={commentsToolTip}
                onCommentsClicked={
                  onCommentsClicked
                    ? () => {
                      onCommentsClicked(
                        ProposalField.FeeScheduleCategory(row.original.id),
                        row.original.name.value,
                      );
                    }
                    : undefined
                }
              />
            )}
          </RowActionsContainer>
        );
      },
      muiTableContainerProps: ({ table }) => {
        const top = table.refs.topToolbarRef.current?.offsetHeight ?? 0;
        const bottom = table.refs.bottomToolbarRef.current?.offsetHeight ?? 0;
        const offset = issues?.length ? "32px" : "-12px";

        return {
          sx: {
            height: `calc(100% - ${top}px - ${bottom}px - ${offset})`,
            minHeight: 0,
          },
        };
      },
      muiTablePaperProps: {
        elevation: 0,
        sx: {
          height: "100%",
        },
      },
    }),
    [
      columns,
      categories,
      hideActionsColumn,
      rowCount,
      columnFilters,
      query,
      isLoading,
      sorting,
      activeReviewField,
      disableCategorySelection,
      beginEditCategory,
      disableEditing,
      hideAddButton,
      beginAddCategory,
      hideExistingButton,
      existingCategories.length,
      handleExistingCategoryClicked,
      hideFromTemplateButton,
      existingTemplates.length,
      handleExistingTemplateClicked,
      hideCommentButton,
      commentThreads,
      disableComments,
      commentsToolTip,
      onCommentsClicked,
      handleRemoveButtonClicked,
      issues?.length,
    ],
  );

  return (
    <div className={className}>
      <MRTNoMemo<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
        options={tableOptions}
      />
    </div>
  );
}
